import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import useResizeObserver from "@react-hook/resize-observer";
import useThrottledCallback from "@shared/utils/hooks/useThrottledCallback";
import { ElementSize } from "@shared/types";

const useMeasure = <E extends HTMLElement | null>(
  ref?: RefObject<E>,
  wait: number = 10,
  options?: {
    leading?: boolean;
    trailing?: boolean;
  }
): [(node: E) => void, ElementSize] => {
  const [target, setTarget] = useState<E | null>(null);
  const localRef = useCallback((node: E | null) => {
    if (node !== null) {
      setTarget(node);
    }
  }, []);
  const [size, setSize] = useState<ElementSize>();

  const safeSetSize = useCallback((size?: ElementSize) => {
    if (size) {
      setSize((previousSize) => {
        const shouldUpdate = Object.entries(size || {}).some(([key, value]) => {
          return value !== previousSize?.[key as keyof ElementSize];
        });
        return shouldUpdate ? { ...previousSize, ...size } : previousSize;
      });
    }
  }, []);

  const throttledSetSize = useThrottledCallback({
    callback: safeSetSize,
    wait,
    options,
  });

  useEffect(() => {
    const node = ref?.current;
    if (node) {
      setTarget((prev) => (prev === node ? prev : node));
    }
  }, [ref?.current]);

  useLayoutEffect(() => {
    if (target) {
      safeSetSize(target.getBoundingClientRect().toJSON());
    }
  }, [target]);

  useResizeObserver(target, (entry: ResizeObserverEntry) => {
    throttledSetSize({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    });
  });

  return [
    localRef,
    size ?? {
      width: 0,
      height: 0,
      x: 0,
      y: 0,
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  ];
};

export default useMeasure;
